import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { PRICE_STOPS } from "@models/productFacet";
import { toggled } from "@utils/toggled";

// URL-encoded sort values: the ones SortPanel offers, which the API honours.
// Figma's popularity and on-sale are hidden — see SortPanel's TODO(backend).
// The type is derived from the list, so a value can't be offered (typed) yet
// be refused by parseSort (listed) — one source (PR 395 review F2).
const SORT_VALUES = ["price-desc", "price-asc"] as const;
export type SortValue = (typeof SORT_VALUES)[number];

export interface SearchFilters {
    sort: SortValue | null;
    categoryIds: number[];
    amounts: string[];
    /** Facet dimensions (#226). null / [] / false each mean "not filtering on this". */
    maxPrice: number | null;
    brands: string[];
    vendorIds: number[];
    inStockOnly: boolean;
}

export interface SearchFiltersAPI extends SearchFilters {
    setSort: (value: SortValue | null) => void;
    toggleCategory: (id: number) => void;
    setMaxPrice: (value: number | null) => void;
    toggleBrand: (value: string) => void;
    toggleVendor: (id: number) => void;
    setInStockOnly: (value: boolean) => void;
    setAll: (next: SearchFilters) => void;
    clearAll: () => void;
}

const SORT_KEY = "sort";
const CATEGORY_KEY = "category";
const AMOUNT_KEY = "amount";
// Multi-value dimensions keep this file's existing convention: a SINGULAR key
// repeated (?brand=Acme&brand=Zed), matching ?category=2&category=5. These are
// the browser's keys and are deliberately not the API's names — `category`
// already becomes `categoryIds` on the wire (see SearchResults.buildSearchUrl).
const MAX_PRICE_KEY = "maxPrice";
const BRAND_KEY = "brand";
// Exported for the browse home's vendor-promotion links (#249), so a link
// built outside this hook spells the key the way the panels read it, and
// only for an id this hook will read back.
export const VENDOR_KEY = "vendor";
export const isVendorId = (n: number): boolean => Number.isInteger(n) && n > 0;
const IN_STOCK_KEY = "inStock";

// Only values ON the price ladder round-trip. An off-ladder number (?maxPrice=37)
// would narrow the results while the slider — which snaps unknown values back to
// "All" — showed no price filter at all, so the control and the grid would
// disagree about what is being filtered. Degrade to "not filtering" instead.
// Also catches the non-numeric, negative and empty cases: none are on the ladder,
// and NaN never reaches the API.
const parseMaxPrice = (raw: string | null): number | null => {
    if (raw === null || raw.trim() === "") return null;
    const value = Number(raw);
    return PRICE_STOPS.some((stop) => stop.maxPrice === value) ? value : null;
};

// Only an offered sort round-trips. Anything else — a bookmarked
// ?sort=popularity or ?sort=on-sale from before they were hidden (audit
// BR-08), or garbage — is no sort, so no option shows as selected and the
// next filter write drops the key.
const parseSort = (raw: string | null): SortValue | null =>
    SORT_VALUES.find((value) => value === raw) ?? null;

const parseFilters = (params: URLSearchParams): SearchFilters => ({
    sort: parseSort(params.get(SORT_KEY)),
    categoryIds: params
        .getAll(CATEGORY_KEY)
        .map(Number)
        .filter((n) => Number.isFinite(n) && n > 0),
    amounts: params.getAll(AMOUNT_KEY),
    maxPrice: parseMaxPrice(params.get(MAX_PRICE_KEY)),
    // An empty ?brand= would ask the API to match products whose brand is the
    // empty string — a constraint the shopper never asked for. Trimmed before the
    // test because a whitespace-only value (?brand=%20, ?brand=%09) is the same
    // non-constraint wearing a disguise: the API drops it via IsNullOrWhiteSpace
    // and returns the unfiltered set, while the panel would draw a blank chip in
    // the SELECTED style with a close glyph — a control claiming a filter that
    // isn't there. Same class parseMaxPrice guards for the price cap.
    brands: params.getAll(BRAND_KEY).filter((b) => b.trim() !== ""),
    // No trim() needed here and none added: Number(" ") and Number("") are both 0,
    // which `n > 0` rejects, and Number("abc") is NaN, which Number.isInteger
    // rejects. Whitespace cannot survive this filter the way it survived the brand
    // one — the numeric coercion is doing the work a string dimension has to do
    // for itself.
    vendorIds: params.getAll(VENDOR_KEY).map(Number).filter(isVendorId),
    // Anything other than the literal we write means "not filtering".
    inStockOnly: params.get(IN_STOCK_KEY) === "true",
});

// Writes filter state into a fresh URLSearchParams, preserving any non-filter
// keys (q, page, etc.) from the current params.
const writeFilters = (
    current: URLSearchParams,
    next: SearchFilters,
): URLSearchParams => {
    const result = new URLSearchParams(current);
    result.delete(SORT_KEY);
    result.delete(CATEGORY_KEY);
    result.delete(AMOUNT_KEY);
    result.delete(MAX_PRICE_KEY);
    result.delete(BRAND_KEY);
    result.delete(VENDOR_KEY);
    result.delete(IN_STOCK_KEY);
    if (next.sort) result.set(SORT_KEY, next.sort);
    next.categoryIds.forEach((id) => result.append(CATEGORY_KEY, String(id)));
    next.amounts.forEach((v) => result.append(AMOUNT_KEY, v));
    // A non-constraining dimension is absent from the URL, never present-and-empty.
    // Note the explicit null check rather than a falsy test: null is the only value
    // that means "no cap", and it must stay distinguishable from any number the
    // ladder might carry.
    if (next.maxPrice !== null) {
        result.set(MAX_PRICE_KEY, String(next.maxPrice));
    }
    next.brands.forEach((b) => result.append(BRAND_KEY, b));
    next.vendorIds.forEach((id) => result.append(VENDOR_KEY, String(id)));
    if (next.inStockOnly) result.set(IN_STOCK_KEY, "true");
    // Any filter change should reset pagination — otherwise users land on a
    // page that no longer exists in the narrowed result set.
    result.delete("page");
    return result;
};

export const useSearchFilters = (): SearchFiltersAPI => {
    const [searchParams, setSearchParams] = useSearchParams();

    const filters = useMemo(() => parseFilters(searchParams), [searchParams]);

    // PUSH is the default, and every discrete write takes it — a chip, a sort, the in-stock
    // toggle, the mobile sheet's Apply. That is this page's long-standing convention and it is
    // what keeps Back meaningful: one press undoes one thing the shopper did.
    //
    // `setMaxPrice` is the single exception and opts in per call. React maps onChange on
    // <input type="range"> to the DOM `input` event, which fires once per intermediate stop while
    // the thumb moves, so pushing would leave a history entry per stop and take five Back presses
    // to undo one drag. Only a CONTINUOUS control has that problem, so only it gets the exception
    // (#226 review F6 — an earlier pass applied replace to every write, which left `setPage` the
    // only pusher on the page and made a single Back leave the search results entirely).
    //
    // Note this is about HISTORY cost and nothing else. It does not reduce the number of writes,
    // and SearchResults' `signal.cancelled` cleanup does not either — that guard suppresses a
    // superseded state WRITE, never the request, which no AbortSignal is passed to and which
    // therefore runs to completion. Request volume is handled by the fetch debounce in
    // SearchResults (FILTER_FETCH_DEBOUNCE_MS). #226 review F3.
    const apply = useCallback(
        (next: SearchFilters, { replace = false }: { replace?: boolean } = {}) => {
            setSearchParams(writeFilters(searchParams, next), { replace });
        },
        [searchParams, setSearchParams],
    );

    const setSort = useCallback(
        (value: SortValue | null) => apply({ ...filters, sort: value }),
        [apply, filters],
    );

    const toggleCategory = useCallback(
        (id: number) => {
            apply({
                ...filters,
                categoryIds: toggled(filters.categoryIds, id),
            });
        },
        [apply, filters],
    );

    // NOTE: `amounts` has no toggle any more — FilterPanel's Amount section was removed for MVP
    // (#226) and its toggle was left orphaned. The dimension itself is still live: parseFilters
    // reads it, writeFilters writes it, clearAll clears it, and FilterSheet carries it across
    // Apply rather than dropping a filter it never offered to edit. Re-add a toggle here when the
    // section comes back post-MVP.

    // The one control that replaces rather than pushes — see the note on `apply`. A drag is one
    // decision expressed as many writes, so it must cost the shopper exactly one Back press.
    const setMaxPrice = useCallback(
        (value: number | null) =>
            apply({ ...filters, maxPrice: value }, { replace: true }),
        [apply, filters],
    );

    const toggleBrand = useCallback(
        (value: string) => {
            apply({ ...filters, brands: toggled(filters.brands, value) });
        },
        [apply, filters],
    );

    const toggleVendor = useCallback(
        (id: number) => {
            apply({ ...filters, vendorIds: toggled(filters.vendorIds, id) });
        },
        [apply, filters],
    );

    const setInStockOnly = useCallback(
        (value: boolean) => apply({ ...filters, inStockOnly: value }),
        [apply, filters],
    );

    const setAll = useCallback((next: SearchFilters) => apply(next), [apply]);

    const clearAll = useCallback(
        () =>
            apply({
                sort: null,
                categoryIds: [],
                amounts: [],
                maxPrice: null,
                brands: [],
                vendorIds: [],
                inStockOnly: false,
            }),
        [apply],
    );

    return {
        ...filters,
        setSort,
        toggleCategory,
        setMaxPrice,
        toggleBrand,
        toggleVendor,
        setInStockOnly,
        setAll,
        clearAll,
    };
};
